import { Link } from 'react-router-dom'
import { ChevronLeft, ChevronDown } from 'lucide-react'
import { useState } from 'react'
import { colors as C, fonts as F } from '../theme'
import { useIsMobile } from '../hooks/useIsMobile'

export default function FaqPage() {
  const [openIdx, setOpenIdx] = useState(null)
  const isMobile = useIsMobile()

  const faqs = [
    {
      q: 'Qu\'est-ce qu\'IAvocat.ai ?',
      a: 'IAvocat.ai est une plateforme SaaS B2B conçue pour les avocats français. Elle combine une intelligence artificielle de pointe et outils collaboratifs pour augmenter la productivité : gestion de dossiers, rédaction assistée d\'actes, agenda intelligent et emails centralisés.',
    },
    {
      q: 'Est-ce que mes données sont sécurisées ?',
      a: 'Oui. Vos données sont hébergées dans l\'Union européenne (Irlande) via Supabase, chiffrées en transit (TLS 1.3) et au repos. Nous respectons le RGPD et n\'utilisons jamais vos données pour entraîner nos modèles IA. Vos dossiers clients et conversations restent confidentiels.',
    },
    {
      q: 'Quels modèles d\'IA sont utilisés ?',
      a: 'IAvocat.ai utilise notre moteur IA propriétaire, spécialisé en droit français. C\'est un assistant IA très capable et sûr. Vos interactions sont traitées conformément à la politique de confidentialité de notre fournisseur IA (30j de rétention max pour amélioration).',
    },
    {
      q: 'Est-ce qu\'IAvocat peut remplacer mon collaborateur ?',
      a: 'Non. IAvocat.ai assiste votre travail mais ne le remplace pas. L\'IA vous aide à rédiger, organiser et gérer vos dossiers, mais c\'est VOUS qui restez responsable de tous les conseils et actes juridiques. Vous devez toujours exercer votre jugement professionnel.',
    },
    {
      q: 'Que se passe-t-il après l\'essai gratuit de 14 jours ?',
      a: 'L\'essai gratuit dure 14 jours et inclut 50 messages et 3 actes ou analyses. À son issue, vous choisissez l\'une de nos trois formules : Essentiel (249€ HT/mois), Cabinet (549€ HT/mois) ou Cabinet+ (1 190€ HT/mois). Vous pouvez annuler à tout moment sans pénalité, et vos données restent exportables.',
    },
    {
      q: 'Puis-je résilier mon abonnement à tout moment ?',
      a: 'Oui. Vous pouvez résilier votre abonnement à tout moment via les paramètres de votre compte. La résiliation prend effet immédiatement. Aucun remboursement n\'est accordé pour les mois partiellement utilisés, mais vous accédez à vos données jusqu\'à la fin du mois.',
    },
    {
      q: 'Quels domaines du droit sont supportés ?',
      a: 'IAvocat.ai supporte tous les domaines du droit français : droit civil, commercial, pénal, administratif, fiscal, droit du travail, droit immobilier, droit des contrats, propriété intellectuelle, etc. L\'IA s\'adapte à votre pratique.',
    },
    {
      q: 'Les formules Cabinet et Cabinet+ sont-elles multi-utilisateurs ?',
      a: 'Pas encore. Au lancement, toutes nos formules fonctionnent avec un compte unique : Essentiel, Cabinet et Cabinet+ sont des paliers de volume (messages et actes mensuels), pensés pour s\'adapter à l\'intensité de votre pratique. Le mode multi-utilisateurs (invitation de collaborateurs, comptes individuels, partage des dossiers) est en préparation et sera proposé prochainement.',
    },
    {
      q: 'IAvocat.ai est-elle certifiée ou agréée ?',
      a: 'IAvocat.ai respecte la réglementation française (Code de l\'organisation judiciaire, secret professionnel avocat-client, RGPD). Elle est hébergée dans l\'Union européenne avec données chiffrées. Cependant, c\'est un outil d\'assistance, pas un cabinet virtuel accrédité.',
    },
  ]

  return (
    <div style={{ background: C.bgPage, minHeight: '100vh' }}>
      {/* Header */}
      <header style={{
        background: C.bgCard,
        borderBottom: `0.5px solid ${C.border}`,
        position: 'sticky',
        top: 0,
        zIndex: 100,
      }}>
        <div style={{
          maxWidth: '900px',
          margin: '0 auto',
          padding: '1rem 2rem',
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
        }}>
          <Link to="/" style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            color: C.textPrimary,
            textDecoration: 'none',
            fontSize: '0.9rem',
            fontWeight: 500,
          }}>
            <ChevronLeft size={20} />
            Retour
          </Link>
          <div style={{
            fontSize: '1.2rem',
            fontFamily: F.serif,
            fontWeight: 400,
            color: C.textPrimary,
          }}>
            IAvocat<span style={{ fontStyle: 'italic', color: C.accent }}>.ai</span>
          </div>
        </div>
      </header>

      {/* Content */}
      <div style={{
        maxWidth: '900px',
        margin: '0 auto',
        padding: isMobile ? '1.5rem 1rem' : '3rem 2rem',
      }}>
        <h1 style={{
          fontFamily: F.serif,
          fontSize: isMobile ? '1.8rem' : '2.5rem',
          fontWeight: 400,
          color: C.textPrimary,
          letterSpacing: '-0.01em',
          marginBottom: '1rem',
        }}>
          Questions Fréquemment Posées
        </h1>
        <p style={{
          fontFamily: F.sans,
          fontSize: '1.1rem',
          color: C.textSecondary,
          marginBottom: '3rem',
          lineHeight: 1.6,
        }}>
          Trouvez les réponses à vos questions sur IAvocat.ai, la sécurité, les fonctionnalités et l'IA.
        </p>

        {/* FAQ Accordion */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem',
        }}>
          {faqs.map((faq, idx) => (
            <div key={idx} style={{
              background: C.bgCard,
              border: `0.5px solid ${C.border}`,
              borderRadius: '8px',
              overflow: 'hidden',
            }}>
              <button
                onClick={() => setOpenIdx(openIdx === idx ? null : idx)}
                style={{
                  width: '100%',
                  padding: isMobile ? '1rem' : '1.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '1rem',
                  background: C.bgCard,
                  border: 'none',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'background-color 0.2s',
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#F5F2EB'}
                onMouseLeave={(e) => e.target.style.backgroundColor = C.bgCard}
              >
                <h3 style={{
                  fontFamily: F.sans,
                  fontSize: '1rem',
                  fontWeight: 500,
                  color: C.textPrimary,
                  margin: 0,
                  flex: 1,
                }}>
                  {faq.q}
                </h3>
                <ChevronDown
                  size={20}
                  style={{
                    color: C.accent,
                    flexShrink: 0,
                    transition: 'transform 0.3s',
                    transform: openIdx === idx ? 'rotate(180deg)' : 'rotate(0deg)',
                  }}
                />
              </button>

              {openIdx === idx && (
                <div style={{
                  padding: isMobile ? '0 1rem 1rem' : '0 1.5rem 1.5rem',
                  borderTop: `0.5px solid ${C.border}`,
                  background: C.bgPage,
                }}>
                  <p style={{
                    fontFamily: F.sans,
                    fontSize: '0.95rem',
                    color: C.textPrimary,
                    lineHeight: 1.8,
                    margin: 0,
                  }}>
                    {faq.a}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* CTA Section */}
        <div style={{
          marginTop: '4rem',
          background: C.bgCard,
          padding: '2.5rem',
          borderRadius: '8px',
          border: `0.5px solid ${C.border}`,
          textAlign: 'center',
        }}>
          <h2 style={{
            fontFamily: F.sans,
            fontSize: '1.3rem',
            fontWeight: 500,
            color: C.textPrimary,
            marginBottom: '1rem',
          }}>
            Vous n'avez pas trouvé la réponse ?
          </h2>
          <p style={{
            fontFamily: F.sans,
            fontSize: '0.95rem',
            color: C.textSecondary,
            marginBottom: '1.5rem',
          }}>
            Contactez-nous directement. Nous répondons à tous les emails dans les 24 heures.
          </p>
          <Link to="/contact" style={{
            display: 'inline-block',
            padding: '0.75rem 1.5rem',
            background: C.accent,
            color: C.bgCard,
            textDecoration: 'none',
            borderRadius: '8px',
            fontFamily: F.sans,
            fontSize: '0.95rem',
            fontWeight: 500,
            transition: 'all 0.2s',
          }}
          onMouseEnter={(e) => {
            e.target.style.opacity = '0.9'
          }}
          onMouseLeave={(e) => {
            e.target.style.opacity = '1'
          }}
          >
            Nous Contacter
          </Link>
        </div>
      </div>
    </div>
  )
}
